import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Button, IconButton,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    Chip, Tooltip, Alert, Snackbar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';

interface Notice {
    id: number;
    title: string;
    author: string;
    content?: string;
    created_at: string;
    updated_at?: string;
    is_important: boolean;
    tags?: string;
    locale?: string;
}

const Notices = () => {
    const [notices, setNotices] = useState<Notice[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [noticeToDelete, setNoticeToDelete] = useState<Notice | null>(null);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const navigate = useNavigate();

    const fetchNotices = async () => {
        setLoading(true);
        try {
            const res = await axios.get('https://roomi.co.kr/api/admin/notices');
            setNotices(res.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching notices:', err);
            setError('공지사항을 불러오는 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotices();
    }, []);

    const handleRowClick = (notice: Notice) => {
        navigate(`/admin/notice/${notice.id}`);
    };

    const handleEdit = (e: React.MouseEvent, notice: Notice) => {
        e.stopPropagation(); // Prevent row click
        navigate(`/admin/notice-form/${notice.id}`);
    };

    const handleDeleteClick = (e: React.MouseEvent, notice: Notice) => {
        e.stopPropagation(); // Prevent row click
        setNoticeToDelete(notice);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!noticeToDelete) return;

        try {
            await axios.delete(`https://roomi.co.kr/api/admin/notices/${noticeToDelete.id}`);
            setNotices(notices.filter(notice => notice.id !== noticeToDelete.id));
            setSnackbar({
                open: true,
                message: '공지사항이 삭제되었습니다.',
                severity: 'success'
            });
        } catch (err) {
            console.error('Error deleting notice:', err);
            setSnackbar({
                open: true,
                message: '공지사항 삭제 중 오류가 발생했습니다.',
                severity: 'error'
            });
        } finally {
            setDeleteDialogOpen(false);
            setNoticeToDelete(null);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    const renderTags = (tagsString?: string) => {
        if (!tagsString) return null;

        try {
            const tags = JSON.parse(tagsString);
            return (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {tags.map((tag: string, index: number) => (
                        <Chip
                            key={index}
                            label={tag}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                        />
                    ))}
                </Box>
            );
        } catch (e) {
            return null;
        }
    };

    return (
        <Box mt={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight="bold">공지사항 목록</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={() => navigate('/admin/notice-form')}
                >
                    새 공지 등록
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TableContainer component={Paper} elevation={3}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.light' }}>
                            <TableCell width="5%" align="center">번호</TableCell>
                            <TableCell width="45%">제목</TableCell>
                            <TableCell width="15%">작성자</TableCell>
                            <TableCell width="15%" align="center">등록일</TableCell>
                            <TableCell width="10%" align="center">중요</TableCell>
                            <TableCell width="10%" align="center">관리</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">로딩 중...</TableCell>
                            </TableRow>
                        ) : notices.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">등록된 공지사항이 없습니다.</TableCell>
                            </TableRow>
                        ) : (
                            notices.map((notice) => (
                                <TableRow
                                    key={notice.id}
                                    hover
                                    onClick={() => handleRowClick(notice)}
                                    sx={{
                                        cursor: 'pointer',
                                        backgroundColor: notice.is_important ? 'rgba(255, 243, 224, 0.5)' : 'inherit'
                                    }}
                                >
                                    <TableCell align="center">{notice.id}</TableCell>
                                    <TableCell>
                                        <Box>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {notice.is_important && (
                                                    <Chip
                                                        label="중요"
                                                        color="error"
                                                        size="small"
                                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                                    />
                                                )}
                                                <Typography fontWeight={notice.is_important ? 'bold' : 'normal'}>
                                                    {notice.title}
                                                </Typography>

                                                {notice.locale && (
                                                    <Chip
                                                        label={notice.locale.toUpperCase()}
                                                        size="small"
                                                        sx={{ height: 20, fontSize: '0.7rem', ml: 1 }}
                                                    />
                                                )}
                                            </Box>
                                            {renderTags(notice.tags)}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{notice.author}</TableCell>
                                    <TableCell align="center">{formatDate(notice.created_at)}</TableCell>
                                    <TableCell align="center">
                                        {notice.is_important ? (
                                            <Tooltip title="중요 공지">
                                                <InfoIcon color="error" fontSize="small" />
                                            </Tooltip>
                                        ) : ''}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box>
                                            <Tooltip title="수정">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => handleEdit(e, notice)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="삭제">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={(e) => handleDeleteClick(e, notice)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>공지사항 삭제</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        "{noticeToDelete?.title}" 공지사항을 삭제하시겠습니까?
                        <br />
                        삭제한 공지사항은 복구할 수 없습니다.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
                        취소
                    </Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">
                        삭제
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Notices;